import { RoleFunction } from '@eagle/common';
import { DashboardType } from '@eagle/core-data-types';
import {
  APP_BANNER_HEIGHT,
  APP_BAR_HEIGHT,
  DashboardAppBarProps,
  dashboardAppBarStyles,
  DashboardSwitcher,
  DashboardSwitcherLocal,
  ErrorPage,
  Icon,
  MiddleSpinner,
  NotFound,
  PageTitle,
  SIDEBAR_FULL_WIDTH,
  SIDEBAR_WIDTH,
  SisenseComposeDashboard,
  SisenseDashboard,
  Undefinable,
  useAuthenticated,
  useBoolFlag,
  useCustomRoutes,
  useHasAuthorization,
  usePromise,
  useSidebarPinContext,
  useTitle
} from '@eagle/react-common';
import { AppBar, Box, Breadcrumbs, Button, Divider, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { Link, Toolbar, useTheme } from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref, useParams } from 'react-router-dom';

const SYSTEM_ADMINISTRATOR_ROLE = [RoleFunction.SYSTEM_ADMINISTRATOR] as const;

const DashboardAppBar = styled(AppBar)<DashboardAppBarProps>(dashboardAppBarStyles);

const V1OuterStack = styled(Stack)({
  height: '100%',
  width: '100%',
  '.showAppBanner & .MuiAppBar-root': { marginTop: `${APP_BANNER_HEIGHT}px` },
});

const OuterStack = styled(Stack)({
  height: '100%',
  width: '100%',
});

const MainStack = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  flexShrink: 0,
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const DashboardBox = styled(Box, { shouldForwardProp: (prop) => prop !== 'applyIframeMargin' })<{
  applyIframeMargin: boolean;
}>(({ applyIframeMargin }) => ({
  ...(applyIframeMargin
    ? {
      '& iframe': {
        marginTop: '14px',
      },
    }
    : undefined),
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const V1ManageButton = styled(Button)(({ theme }) => ({
  color: theme.palette.common.white,
  display: 'inline-flex',
  alignItems: 'center',
}));

export const DashboardPage: FC = () => {
  const { t } = useTranslation(['track']);
  const { isPinned } = useSidebarPinContext();
  const { restClient } = useAuthenticated();
  const { hasAuthorization } = useHasAuthorization();
  const { dashboardId } = useParams();
  const { dashboards } = useCustomRoutes();
  const [dashboardName, setDashboardName] = useState<Undefinable<string>>();
  const hasSystemAdminPermissions = hasAuthorization(SYSTEM_ADMINISTRATOR_ROLE);
  const v2DashSwitching = useBoolFlag('portals-dashboard-switching-enhancements-temporary-20240918') ?? false;
  const sisenseComposeSdkEnabled = useBoolFlag('portals-sisense-compose-sdk-feature') ?? false;

  const renderTitle = useCallback(() => {
    return (
      <DashboardSwitcher dashboardId={dashboardId} type={DashboardType.DASHBOARD} sx={{ flex: 1, justifyItems: 'right', mr: -0.5 }}>
        <Typography noWrap variant="body1" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" mr={1}>
          {dashboardName}
        </Typography>
      </DashboardSwitcher>
    );
  }, [dashboardName, dashboardId]);
  useTitle(renderTitle(), true);
  const theme = useTheme();
  const href = useHref(`/${dashboards}`);
  const manageHref = useHref(dashboardId ? `/${dashboards}/manage/${dashboardId}` : '');

  const [dashboard, dashboardError, dashboardState] = usePromise(async () => {
    const filter = { '_id': dashboardId };
    const result = await restClient.my.dashboards.getAllV2({
      filter,
    });
    return result?.items.length ? result?.items[0] : undefined;
  }, [dashboardId, restClient]);

  useEffect(() => {
    if (!dashboard) return;
    setDashboardName(dashboard.display);
  }, [dashboard]);

  if (dashboardState === 'pending') return <MiddleSpinner />;
  if (dashboardError) return <ErrorPage error={dashboardError} />;
  if (!dashboard) return <NotFound />;

  if (v2DashSwitching) {
    return (
      <OuterStack>
        <MainStack
          alignItems="center"
          direction="row"
        >
          <Stack spacing={1} flex={1} >
            <PageTitle title={dashboardName} icon={<Icon icon="DashboardIcon" />} />
            <DashboardSwitcherLocal type={DashboardType.DASHBOARD} dashboardId={dashboardId}>
              <Typography variant="body2" ml={5.2}>{t('common:component.navigation.switch-to.labels')}</Typography>
            </DashboardSwitcherLocal>
          </Stack>
          {hasSystemAdminPermissions && manageHref
            && <Box>
              <Button href={manageHref} >
                {t('track:page.dashboards.manage.action')}
              </Button>
            </Box>
          }
        </MainStack>
        <Divider />
        <DashboardBox applyIframeMargin={!v2DashSwitching}>
          {sisenseComposeSdkEnabled ?
            <SisenseComposeDashboard type='dashboards' dashboardId={dashboardId ?? 'error'} />
            :
            <SisenseDashboard type='dashboards' dashboardId={dashboardId ?? 'error'} />
          }
        </DashboardBox>
      </OuterStack>
    );
  }

  return (
    <V1OuterStack>
      <MainStack
        alignItems="center"
        direction="row"
      >
        <DashboardAppBar
          height={APP_BAR_HEIGHT}
          isPinned={isPinned}
          sidebarFullWidth={SIDEBAR_FULL_WIDTH}
          sidebarWidth={SIDEBAR_WIDTH}
        >
          <Toolbar sx={{ spacing: 1, flex: 1 }}>
            <Breadcrumbs color={theme.palette.common.white}>
              <Link color={theme.palette.common.white} href={href}>
                {t('track:page.dashboards.title')}
              </Link>
              <DashboardSwitcher dashboardId={dashboardId} type={DashboardType.DASHBOARD}>
                <Typography>{dashboardName}</Typography>
              </DashboardSwitcher>
            </Breadcrumbs>
          </Toolbar>
          {hasSystemAdminPermissions && manageHref
            && <Box>
              <V1ManageButton href={manageHref}>
                {t('track:page.dashboards.manage.action')}
              </V1ManageButton>
            </Box>
          }
        </DashboardAppBar>
      </MainStack>
      <DashboardBox applyIframeMargin={!v2DashSwitching}>
        {sisenseComposeSdkEnabled ?
          <SisenseComposeDashboard type='dashboards' dashboardId={dashboardId ?? 'error'} />
          :
          <SisenseDashboard type='dashboards' dashboardId={dashboardId ?? 'error'} />
        }
      </DashboardBox>
    </V1OuterStack>
  );
};
